import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { Button, BadgeAlt as Badge, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Popover, PopoverContent, PopoverTrigger, Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, } from '@cargomatic/ui';
import { Plus, X, ChevronsUpDown, Info, Check } from 'lucide-react';
import { addLegSchema, validateEquipmentFormat, } from './schemas';
import { weightUnitsOptions } from 'utils/localization/types';
import { useTranslation } from 'react-i18next';
import LocationAutosuggest from 'components/site/location-autosuggest';
import CustomerSearch from 'components/site/customer-search';
export const AddLegModal = ({ isOpen, onClose, onSubmit, orderEquipment, editingShipment, defaultValues, }) => {
    const AnyCommandItem = CommandItem;
    const [showEquipmentSuggestions, setShowEquipmentSuggestions] = useState(null);
    const [equipmentSearchValue, setEquipmentSearchValue] = useState('');
    const [equipmentWarnings, setEquipmentWarnings] = useState({});
    const [suppressFormatWarnings, setSuppressFormatWarnings] = useState({});
    const { register, control, handleSubmit, watch, setValue, reset, clearErrors, setError, formState: { errors }, } = useForm({
        defaultValues: defaultValues || {
            legType: 'full_container',
            equipment: [{ assetType: 'container', equipmentNumber: '' }],
            totalWeight: '',
            weightUnit: 'lb',
            pickupName: '',
            pickupAddress: '',
            deliverName: '',
            deliverAddress: '',
            carrier: '',
            specialInstructions: '',
        },
    });
    const { t } = useTranslation();
    const [carrierDisplayName, setCarrierDisplayName] = useState('');
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'equipment',
    });
    const [initialEquipmentCount, setInitialEquipmentCount] = useState(0);
    const watchedEquipment = watch('equipment');
    const [isSubmitting, setIsSubmitting] = useState(false);
    useEffect(() => {
        const warnings = {};
        watchedEquipment.forEach((equipment, index) => {
            if (suppressFormatWarnings[index]) {
                return;
            }
            if (equipment.equipmentNumber && equipment.assetType) {
                const warning = validateEquipmentFormat(equipment.equipmentNumber, equipment.assetType);
                if (warning) {
                    warnings[index] = warning;
                }
            }
        });
        setEquipmentWarnings(warnings);
    }, [watchedEquipment, suppressFormatWarnings]);
    useEffect(() => {
        if (isOpen) {
            if (defaultValues) {
                reset(Object.assign(Object.assign({}, defaultValues), { weightUnit: defaultValues.weightUnit }));
                setInitialEquipmentCount((defaultValues.equipment || []).length || 0);
            }
            else {
                setInitialEquipmentCount(0);
            }
        }
        else {
            reset();
            setEquipmentWarnings({});
            setIsSubmitting(false);
            setCarrierDisplayName('');
            setShowEquipmentSuggestions(null);
            setEquipmentSearchValue('');
        }
    }, [isOpen, reset]);
    // When editing, prefill the carrier display name from the shipment
    useEffect(() => {
        if (isOpen && editingShipment) {
            setCarrierDisplayName(editingShipment.carrier_name);
        }
    }, [isOpen, editingShipment]);
    const handleFormSubmit = (data) => {
        // Guard: each equipment row must have a number
        let hasEquipmentError = false;
        (data.equipment || []).forEach((eq, i) => {
            const value = ((eq === null || eq === void 0 ? void 0 : eq.equipmentNumber) || '').trim();
            if (value.length === 0) {
                const fieldPath = `equipment.${i}.equipmentNumber`;
                setError(fieldPath, {
                    type: 'manual',
                    message: 'Equipment number is required',
                });
                hasEquipmentError = true;
            }
        });
        const result = addLegSchema.safeParse(data);
        if (!result.success) {
            result.error.issues.forEach((issue) => {
                const pathJoined = (issue.path || []).join('.');
                if (pathJoined) {
                    const fieldPath = pathJoined;
                    setError(fieldPath, { type: 'manual', message: issue.message });
                }
            });
        }
        if (hasEquipmentError || !result.success)
            return;
        setIsSubmitting(true);
        onSubmit(result.data);
        // Do not reset here; parent will close the modal after API completes.
    };
    const handleSelectEquipment = (index, equipmentId, containerType) => {
        setValue(`equipment.${index}.equipmentNumber`, equipmentId, {
            shouldDirty: true,
            shouldValidate: true,
        });
        const normalizedType = `${containerType || 'container'}`.toLowerCase();
        setValue(`equipment.${index}.assetType`, normalizedType, {
            shouldDirty: true,
            shouldValidate: true,
        });
        clearErrors([
            `equipment.${index}.equipmentNumber`,
            `equipment.${index}.assetType`,
        ]);
        setShowEquipmentSuggestions(null);
        setEquipmentSearchValue('');
        // Suppress format warning for known existing equipment selections
        setSuppressFormatWarnings((prev) => (Object.assign(Object.assign({}, prev), { [index]: true })));
        setEquipmentWarnings((prev) => {
            const next = Object.assign({}, prev);
            delete next[index];
            return next;
        });
    };
    return (React.createElement(Dialog, { open: isOpen, onOpenChange: (open) => {
            if (!open) {
                onClose();
            }
        } },
        React.createElement(DialogContent, { className: "max-w-2xl max-h-[85vh] overflow-y-auto" },
            React.createElement(DialogHeader, null,
                React.createElement(DialogTitle, null, editingShipment ? 'Edit Leg' : 'Add Leg'),
                React.createElement(DialogDescription, null, editingShipment
                    ? 'Update the leg details below.'
                    : 'Add a new leg to the order. Fill in the details below.')),
            React.createElement("form", { onSubmit: handleSubmit(handleFormSubmit), className: "grid gap-4 py-4" },
                React.createElement("div", { className: "grid gap-2" },
                    React.createElement(Label, { htmlFor: "legType" }, "Leg Type"),
                    React.createElement(Select, { value: watch('legType'), onValueChange: (value) => setValue('legType', value), disabled: !!editingShipment },
                        React.createElement(SelectTrigger, { id: "legType", className: "w-full" },
                            React.createElement(SelectValue, { placeholder: "Select leg type" })),
                        React.createElement(SelectContent, null,
                            React.createElement(SelectItem, { value: "full_container" }, "Full Container"),
                            React.createElement(SelectItem, { value: "empty_container" }, "Empty Container"),
                            React.createElement(SelectItem, { value: "chassis_split" }, "Chassis Split"),
                            React.createElement(SelectItem, { value: "bobtail" }, "Bobtail"),
                            React.createElement(SelectItem, { value: "dry_run" }, "Dry Run"))),
                    errors.legType && (React.createElement("p", { className: "text-sm text-red-500" }, errors.legType.message))),
                React.createElement("div", { className: "grid gap-3" },
                    React.createElement("div", { className: "flex items-center justify-between" },
                        React.createElement(Label, null, "Equipment"),
                        React.createElement(Button, { type: "button", variant: "outline", size: "sm", onClick: () => append({ assetType: 'container', equipmentNumber: '' }) },
                            React.createElement(Plus, { className: "h-4 w-4 mr-1" }),
                            "Add Equipment")),
                    React.createElement("div", { className: "space-y-3" }, fields.map((field, index) => {
                        var _a, _b, _c, _d, _e, _f, _g, _h;
                        return (React.createElement("div", { key: field.id, className: "flex gap-3 items-start p-3 border rounded-lg" },
                            React.createElement("div", { className: "flex-1 grid grid-cols-2 gap-3 items-start" },
                                React.createElement("div", { className: "grid gap-2" },
                                    React.createElement(Label, { htmlFor: `equipment.${index}.assetType`, className: "text-xs" }, "Asset Type"),
                                    React.createElement(Select, { value: watch(`equipment.${index}.assetType`), onValueChange: (value) => setValue(`equipment.${index}.assetType`, value), disabled: !!editingShipment && index < initialEquipmentCount },
                                        React.createElement(SelectTrigger, { className: "w-full" },
                                            React.createElement(SelectValue, { placeholder: "Select type" })),
                                        React.createElement(SelectContent, null,
                                            React.createElement(SelectItem, { value: "container" }, "Container"),
                                            React.createElement(SelectItem, { value: "chassis" }, "Chassis"),
                                            React.createElement(SelectItem, { value: "trailer" }, "Trailer"))),
                                    ((_b = (_a = errors.equipment) === null || _a === void 0 ? void 0 : _a[index]) === null || _b === void 0 ? void 0 : _b.assetType) && (React.createElement("p", { className: "text-xs text-red-500" }, (_d = (_c = errors.equipment[index]) === null || _c === void 0 ? void 0 : _c.assetType) === null || _d === void 0 ? void 0 : _d.message))),
                                React.createElement("div", { className: "grid gap-2 relative" },
                                    React.createElement(Label, { htmlFor: `equipment.${index}.equipmentNumber`, className: "text-xs" }, "Equipment Number"),
                                    React.createElement(Popover, { open: (!editingShipment ||
                                            index >= initialEquipmentCount) &&
                                            showEquipmentSuggestions === index, onOpenChange: (open) => {
                                            if (editingShipment && index < initialEquipmentCount)
                                                return;
                                            setShowEquipmentSuggestions(open ? index : null);
                                            if (!open)
                                                setEquipmentSearchValue('');
                                        } },
                                        React.createElement(PopoverTrigger, { asChild: true },
                                            React.createElement(Button, { type: "button", variant: "outline", role: "combobox", "aria-expanded": (!editingShipment ||
                                                    index >= initialEquipmentCount) &&
                                                    showEquipmentSuggestions === index, className: "w-full justify-between font-normal bg-transparent", disabled: !!editingShipment && index < initialEquipmentCount },
                                                React.createElement("span", { className: watch(`equipment.${index}.equipmentNumber`)
                                                        ? ''
                                                        : 'text-muted-foreground' }, watch(`equipment.${index}.equipmentNumber`) ||
                                                    'Select equipment...'),
                                                React.createElement(ChevronsUpDown, { className: "ml-2 h-4 w-4 shrink-0 opacity-50" }))),
                                        React.createElement("input", Object.assign({ type: "hidden" }, register(`equipment.${index}.equipmentNumber`))),
                                        React.createElement("input", Object.assign({ type: "hidden" }, register(`equipment.${index}.assetType`))),
                                        React.createElement(PopoverContent, { className: "w-[400px] p-0", side: "bottom", align: "start" },
                                            React.createElement(Command, null,
                                                React.createElement(CommandInput, { onValueChange: setEquipmentSearchValue, placeholder: "Search equipment numbers...", className: "h-9" }),
                                                React.createElement(CommandList, { className: "max-h-[200px] overflow-auto" }, orderEquipment.filter((eq) => !equipmentSearchValue ||
                                                    eq.equipment_id
                                                        .toLowerCase()
                                                        .includes(equipmentSearchValue.toLowerCase())).length > 0 ? (React.createElement(CommandGroup, { heading: "Order Equipment" }, orderEquipment
                                                    .filter((eq) => !equipmentSearchValue ||
                                                    eq.equipment_id
                                                        .toLowerCase()
                                                        .includes(equipmentSearchValue.toLowerCase()))
                                                    .map((equipment, itemIndex) => (React.createElement(AnyCommandItem, { key: equipment.equipment_id, value: equipment.equipment_id, index: itemIndex, item: equipment.equipment_id, onSelect: () => handleSelectEquipment(index, equipment.equipment_id, equipment._equipmentType) },
                                                    React.createElement("div", { className: "flex items-center gap-2", onMouseDown: (e) => {
                                                            // Fallback in case onSelect is not firing
                                                            e.preventDefault();
                                                            handleSelectEquipment(index, equipment.equipment_id, equipment._equipmentType);
                                                        } },
                                                        React.createElement(Check, { className: `h-4 w-4 ${watch(`equipment.${index}.equipmentNumber`) === equipment.equipment_id
                                                                ? 'opacity-100'
                                                                : 'opacity-0'}` }),
                                                        React.createElement("span", null, equipment.equipment_id),
                                                        React.createElement(Badge, { variant: "outline", className: "text-xs" }, equipment._equipmentType))))))) : (React.createElement(CommandEmpty, null,
                                                    React.createElement("div", { className: "px-2" },
                                                        React.createElement(Button, { type: "button", size: "sm", variant: "secondary", className: "w-full justify-start", onClick: () => {
                                                                const value = (equipmentSearchValue || '').trim();
                                                                // Ensure an asset type exists so format validation can run
                                                                const currentType = watch(`equipment.${index}.assetType`);
                                                                const normalizedType = (currentType ||
                                                                    'container');
                                                                // Allow warnings to show for user-created entries
                                                                setSuppressFormatWarnings((prev) => {
                                                                    const next = Object.assign({}, prev);
                                                                    delete next[index];
                                                                    return next;
                                                                });
                                                                setValue(`equipment.${index}.assetType`, normalizedType, {
                                                                    shouldDirty: true,
                                                                    shouldValidate: true,
                                                                });
                                                                setValue(`equipment.${index}.equipmentNumber`, value, {
                                                                    shouldDirty: true,
                                                                    shouldValidate: true,
                                                                });
                                                                clearErrors([
                                                                    `equipment.${index}.equipmentNumber`,
                                                                    `equipment.${index}.assetType`,
                                                                ]);
                                                                setShowEquipmentSuggestions(null);
                                                                setEquipmentSearchValue('');
                                                                // Immediately compute and show validation warning
                                                                const warn = validateEquipmentFormat(value, normalizedType) || '';
                                                                setEquipmentWarnings((prev) => {
                                                                    const next = Object.assign({}, prev);
                                                                    if (warn) {
                                                                        next[index] = warn;
                                                                    }
                                                                    else {
                                                                        delete next[index];
                                                                    }
                                                                    return next;
                                                                });
                                                            } },
                                                            React.createElement(Plus, { className: "mr-2 h-4 w-4" }),
                                                            "Create \"",
                                                            equipmentSearchValue,
                                                            "\"")))))))),
                                    equipmentWarnings[index] && (React.createElement("p", { className: "text-xs text-yellow-600" },
                                        "\u26A0\uFE0F ",
                                        equipmentWarnings[index],
                                        " \u2014 10-11 characters, tc")),
                                    ((_f = (_e = errors.equipment) === null || _e === void 0 ? void 0 : _e[index]) === null || _f === void 0 ? void 0 : _f.equipmentNumber) && (React.createElement("p", { className: "text-xs text-red-500" }, (_h = (_g = errors.equipment[index]) === null || _g === void 0 ? void 0 : _g.equipmentNumber) === null || _h === void 0 ? void 0 : _h.message)))),
                            fields.length > 1 && (React.createElement(Button, { type: "button", variant: "ghost", size: "sm", className: "h-8 w-8 p-0 mt-6", onClick: () => remove(index) },
                                React.createElement(X, { className: "h-4 w-4" })))));
                    })),
                    errors.equipment && (React.createElement("p", { className: "text-sm text-red-500" }, errors.equipment.message))),
                React.createElement("div", { className: "grid gap-2" },
                    React.createElement("div", { className: "flex items-center gap-2" },
                        React.createElement(Label, { htmlFor: "totalWeight" }, "Total Weight"),
                        React.createElement(TooltipProvider, null,
                            React.createElement(Tooltip, null,
                                React.createElement(TooltipTrigger, { asChild: true },
                                    React.createElement(Info, { className: "h-4 w-4 text-muted-foreground cursor-help" })),
                                React.createElement(TooltipContent, null,
                                    React.createElement("p", null, "Please enter the product weight."))))),
                    React.createElement("div", { className: "grid grid-cols-2 gap-3" },
                        React.createElement("div", { className: "grid gap-2" },
                            React.createElement(Input, Object.assign({ id: "totalWeight", type: "number", placeholder: "Enter weight" }, register('totalWeight'))),
                            errors.totalWeight && (React.createElement("p", { className: "text-xs text-red-500" }, errors.totalWeight.message))),
                        React.createElement("div", { className: "grid gap-2" },
                            React.createElement(Select, { value: watch('weightUnit'), onValueChange: (value) => setValue('weightUnit', value) },
                                React.createElement(SelectTrigger, { className: "w-full" },
                                    React.createElement(SelectValue, { placeholder: "Select unit" })),
                                React.createElement(SelectContent, null, weightUnitsOptions.map((u) => (React.createElement(SelectItem, { key: u.value, value: u.value }, t(u.localizationSlug))))))))),
                React.createElement("div", { className: "grid gap-2" },
                    React.createElement(Label, { htmlFor: "pickupName" }, "Pickup Location"),
                    React.createElement(Input, Object.assign({ id: "pickupName", placeholder: "Location name" }, register('pickupName'))),
                    errors.pickupName && (React.createElement("p", { className: "text-xs text-red-500" }, errors.pickupName.message)),
                    React.createElement("div", { className: "relative" },
                        React.createElement(LocationAutosuggest, { id: "pickupAddress", value: watch('pickupAddress'), placeholderText: "START_TYPING_TO_SEARCH_FOR_A_LOCATION", searchField: "address", autoHighlight: false, callbackSelectedItem: (item) => {
                                const currentName = watch('pickupName');
                                const nextName = (item === null || item === void 0 ? void 0 : item.name) && item.name.trim().length > 0
                                    ? item.name
                                    : currentName;
                                if (nextName !== undefined) {
                                    setValue('pickupName', nextName, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    });
                                }
                                setValue('pickupAddress', (item === null || item === void 0 ? void 0 : item.address) || '', {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                                clearErrors(['pickupName', 'pickupAddress']);
                            }, onChange: (value) => setValue('pickupAddress', value, {
                                shouldDirty: true,
                                shouldValidate: true,
                            }), dropdownStyle: { maxHeight: '200px', overflow: 'auto' } })),
                    errors.pickupAddress && (React.createElement("p", { className: "text-xs text-red-500" }, errors.pickupAddress.message))),
                React.createElement("div", { className: "grid gap-2" },
                    React.createElement(Label, { htmlFor: "deliverName" }, "Deliver Location"),
                    React.createElement(Input, Object.assign({ id: "deliverName", placeholder: "Location name" }, register('deliverName'))),
                    errors.deliverName && (React.createElement("p", { className: "text-xs text-red-500" }, errors.deliverName.message)),
                    React.createElement("div", { className: "relative" },
                        React.createElement(LocationAutosuggest, { id: "deliverAddress", value: watch('deliverAddress'), placeholderText: "START_TYPING_TO_SEARCH_FOR_A_LOCATION", searchField: "address", autoHighlight: false, callbackSelectedItem: (item) => {
                                const currentName = watch('deliverName');
                                const nextName = (item === null || item === void 0 ? void 0 : item.name) && item.name.trim().length > 0
                                    ? item.name
                                    : currentName;
                                if (nextName !== undefined) {
                                    setValue('deliverName', nextName, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    });
                                }
                                setValue('deliverAddress', (item === null || item === void 0 ? void 0 : item.address) || '', {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                                clearErrors(['deliverName', 'deliverAddress']);
                            }, onChange: (value) => setValue('deliverAddress', value, {
                                shouldDirty: true,
                                shouldValidate: true,
                            }), dropdownStyle: { maxHeight: '200px', overflow: 'auto' } })),
                    errors.deliverAddress && (React.createElement("p", { className: "text-xs text-red-500" }, errors.deliverAddress.message))),
                React.createElement("div", { className: "grid gap-2" },
                    React.createElement(Label, { htmlFor: "carrier" }, "Carrier"),
                    React.createElement("div", { id: "order-carrier-search-container", className: "customer-search" },
                        React.createElement(CustomerSearch, { clearOnSelect: false, placeholder: t('SEARCH_FOR_CARRIER'), defaultValue: carrierDisplayName, type: "carriers", onSelect: (_value, item) => {
                                const name = (item === null || item === void 0 ? void 0 : item.name) || (item === null || item === void 0 ? void 0 : item.company_name) || '';
                                setValue('carrier', (item === null || item === void 0 ? void 0 : item._id) || '', {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                                setCarrierDisplayName(name);
                                clearErrors('carrier');
                            }, onChange: (value) => setCarrierDisplayName(value), menuStyle: { position: 'absolute', top: 40, left: 0 } })),
                    errors.carrier && (React.createElement("p", { className: "text-xs text-red-500" }, errors.carrier.message))),
                !editingShipment && (React.createElement("div", { className: "grid gap-2" },
                    React.createElement(Label, { htmlFor: "specialInstructions" }, "Special Instructions"),
                    React.createElement("textarea", Object.assign({ id: "specialInstructions", placeholder: "Enter any special instructions or notes for this leg...", className: "min-h-24 w-full border rounded px-3 py-2 bg-background" }, register('specialInstructions'))),
                    (errors === null || errors === void 0 ? void 0 : errors.specialInstructions) && (React.createElement("p", { className: "text-xs text-red-500" }, errors.specialInstructions.message)))),
                React.createElement(DialogFooter, null,
                    React.createElement(Button, { type: "button", variant: "outline", onClick: onClose, disabled: isSubmitting }, "Cancel"),
                    React.createElement(Button, { type: "submit", variant: "default", disabled: isSubmitting }, isSubmitting
                        ? editingShipment
                            ? 'Updating…'
                            : 'Adding…'
                        : editingShipment
                            ? 'Update Leg'
                            : 'Add Leg'))))));
};
